import { styled, TableCellBaseProps, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { LoadState } from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { capitalize } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import GpsStatusPopover from '~components/Common/GpsStatusPopover';
import JobStatusChip from '~components/Job/JobStatusChip';
import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { Job } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import { Load } from '~hooks/useLoad/models';
import { useStores } from '~store';
import { dateFormat } from '~utils/dateTime';

const makeDeliveredLoadsCellString = (loads?: Load[]) => {
  if (!loads) {
    return;
  }

  const deliveredLoads = loads.filter(
    (load) => load.state === LoadState.COMPLETED,
  ).length;
  const totalLoads = loads.length;

  return `${deliveredLoads} / ${totalLoads}`;
};

const makeDeliveredQuantityCellString = (loads?: Load[]) => {
  if (!loads) {
    return;
  }

  const [deliveredQuantity, totalQuantity] = loads.reduce(
    ([delivered, total], load) => {
      const quantity = load.quantity;

      total += quantity;

      if (load.state === LoadState.COMPLETED) {
        delivered += quantity;
      }

      return [delivered, total];
    },
    [0, 0],
  );

  return `${deliveredQuantity} / ${totalQuantity}`;
};

interface OrderJobListProps {
  onSelect: (job: Job) => void;
  onViewRoute: (job: Job) => void;
  orderId: string;
  selectedJobId?: string;
}

export const OrderJobList = observer(
  ({ onSelect, onViewRoute, orderId, selectedJobId }: OrderJobListProps) => {
    const theme = useTheme();
    const { liveMapStoreNew, userStore } = useStores();
    const { getJobLoads, getJobDeviceStatus } = useJob();
    const currentCompany = userStore.currentCompanies[0] || userStore.userCompany;

    const [jobLoads, setJobLoads] = useState<{
      isLoading: boolean;
      jobLoads?: Map<string, Load[]>;
    }>({ isLoading: true });

    const jobs = liveMapStoreNew.orderJobs.get(orderId) ?? [];
    const jobIds = jobs.map((job) => job.id);

    useEffect(() => {
      if (!jobIds.length) {
        return;
      }

      const jobLoadsMap = new Map<string, Load[]>();
      jobIds.forEach((id) => jobLoadsMap.set(id, []));

      const jobLoadsRequests = jobIds.map((jobId) => getJobLoads(jobId));

      Promise.all(jobLoadsRequests).then((responses) => {
        responses.forEach((jobLoads, index) => {
          jobLoadsMap.set(jobIds[index], jobLoads);
        });

        setJobLoads({ isLoading: false, jobLoads: jobLoadsMap });
      });
    }, [JSON.stringify(jobIds)]);

    return (
      <Box
        sx={{
          backgroundColor: theme.brandV2.colors.treadGray8,
          border: `1px solid ${theme.brandV2.colors.treadGray7}`,
          overflow: 'hidden',
          borderRadius: theme.brandV2.borderRadius,
          mt: 1,
        }}
      >
        <Collapse in={jobs.length > 0}>
          {!jobLoads.isLoading && (
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell isHeader>{t('form_fields.time')}</StyledTableCell>
                  <StyledTableCell isHeader>{t('live_map.truck_vendor')}</StyledTableCell>
                  <StyledTableCell isHeader>
                    {capitalize(`${t('common.delivered')}`)}
                  </StyledTableCell>
                  <StyledTableCell isHeader>{t('form_fields.status')}</StyledTableCell>
                  <StyledTableCell isHeader>{t('live_map.gps_data')}</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ backgroundColor: theme.semantic.surfaceRaised }}>
                {jobs.map((job) => (
                  <TableRow
                    key={job.id}
                    onClick={() => onSelect(job)}
                    sx={{
                      borderLeft:
                        selectedJobId === job.id
                          ? `2px solid ${theme.palette.info.main}`
                          : 'none',
                    }}
                  >
                    <StyledTableCell>
                      <Typography
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          fontSize: 'inherit',
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{
                            fontSize: 'inherit',
                          }}
                        >
                          {dateFormat(dayjs(job.jobStartAt), 'hh:mmA')}
                        </Typography>

                        <Typography
                          component="span"
                          color={theme.brandV2.colors.treadGray3}
                          sx={{
                            fontSize: 'inherit',
                          }}
                        >
                          {job.jobId}
                        </Typography>
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell>
                      <Typography
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          fontSize: 'inherit',
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{
                            fontSize: 'inherit',
                          }}
                        >
                          {job.equipment?.name}
                        </Typography>

                        <Typography
                          component="span"
                          color={theme.brandV2.colors.treadGray3}
                          sx={{
                            fontSize: 'inherit',
                          }}
                        >
                          {job.vendorJobAssignment?.vendorAccount?.name}
                        </Typography>
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell>
                      <Typography
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          fontSize: 'inherit',
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{
                            fontSize: 'inherit',
                          }}
                        >
                          {`${makeDeliveredLoadsCellString(jobLoads.jobLoads?.get(job.id))} ${t('order.form.loads')}`}
                        </Typography>
                        <Typography
                          component="span"
                          color={theme.brandV2.colors.treadGray3}
                          sx={{
                            fontSize: 'inherit',
                          }}
                        >
                          {makeDeliveredQuantityCellString(
                            jobLoads.jobLoads?.get(job.id),
                          )}{' '}
                          {job.unitOfMeasure?.name}
                        </Typography>
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell>
                      <JobStatusChip jobState={job.status} hideDropdownArrow />
                    </StyledTableCell>

                    <StyledTableCell
                      align="right"
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                      }}
                    >
                      {job.gpsStatus && (
                        <GpsStatusPopover
                          gpsStatus={job.gpsStatus}
                          showIdleBadge={false}
                          onGetDeviceStatus={() =>
                            getJobDeviceStatus(job.id, currentCompany.id)
                          }
                        />
                      )}

                      <Button
                        variant="text"
                        size="small"
                        color="info"
                        onClick={(e) => {
                          onViewRoute(job);
                          e.stopPropagation();
                        }}
                        style={{
                          marginInline: 0,
                          paddingInline: '0px',
                          minWidth: 'unset',
                        }}
                      >
                        {t('actions.view')}
                      </Button>
                    </StyledTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Collapse>
        <Box
          sx={{
            bgcolor: theme.semantic.surfaceRaised,
          }}
        >
          <LoadingSpinner isVisible={jobLoads.isLoading} sx={{ py: 2 }} />
        </Box>
      </Box>
    );
  },
);

interface StyledTableCellProps extends TableCellBaseProps {
  isHeader?: boolean;
}
const StyledTableCell = styled(TableCell, {
  shouldForwardProp: (propName) => propName !== 'isHeader',
})<StyledTableCellProps>(({ isHeader, theme }) => ({
  '&.MuiTableCell-root': {
    padding: theme.spacing(0.5),
    border: 0,
    fontSize: isHeader ? '12px' : '10px',
    textAlign: 'center',
    verticalAlign: 'middle',
    ...(isHeader
      ? {
          color: theme.brandV2.colors.treadGray3,
          fontWeight: 600,
          borderBottom: `1px solid ${theme.brandV2.colors.treadGray7}`,
        }
      : {}),
  },
}));
